import React, { useCallback, useEffect, useState } from 'react';
import { Box, InlineDatePicker, makeStyles, Skeleton, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import moment, { Moment } from 'moment';

import { TimeSlotDict } from '../../../api/api';
import { Layout } from '../../../components/Layout';
import { ScrollableContent } from '../../../components/ScrollableContent';
import { StepProgressBar } from '../../../components/StepProgressBar';
import { useChangeStep } from '../../../hooks/useChangeStep';
import { useOrgData } from '../../../hooks/useOrgData';
import { useTimeSlotsV2 } from '../../../hooks/useTimeSlotsV2';
import { useOnlineBookingReducer } from '../../../state/useOnlineBookingReducer';
import { getPricingFormItemsFromCart, getServiceItemsFromCart } from '../../../state/utils';
import { BackArrowButton } from '../../BackArrowButton';
import { WarningMessage } from '../../WarningMessage';

import { DayPicker } from './DayPicker';
import { TimeSlots } from './TimeSlots';

const useStyles = makeStyles((theme: HCPTheme) => ({
  input: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow:
      '0px 0px 12px 2px rgba(0, 0, 0, .1), 0px 0px 0px 0px rgba(0, 0, 0, .1), 0px 0px 0px 0px rgba(0, 0, 0, .1)',
    '& fieldset': {
      border: 'none',
    },
  },
}));

const DATE_FORMAT = 'dddd Do, MMMM YYYY';

const earliestDate = (previousDate: string, nextDate: string) =>
  new Date(previousDate).getTime() - new Date(nextDate).getTime();

const findFirstAvailableDate = (timeSlots: TimeSlotDict) => {
  return Object.keys(timeSlots)
    .sort(earliestDate)
    .find((date) => timeSlots[date]?.length);
};

const findDateToSelect = (timeSlots: TimeSlotDict) => {
  const currentDate = moment(new Date()).format('YYYY-MM-DD');

  if (timeSlots[currentDate]?.length) {
    return currentDate;
  }

  const dateToSelect = findFirstAvailableDate(timeSlots);

  return dateToSelect || null;
};

export const ArrivalStepV2 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { input } = useStyles();
  const { changeStep } = useChangeStep();

  const { data: orgData } = useOrgData({ token: state.token });
  const selectedDate = state.arrivalWindow.date;

  const setDate = useCallback(
    (date: string) => {
      dispatch(actions.selectArrivalDate(date));
      dispatch(actions.resetArrivalTime());
      dispatch(actions.setUnavailableDateError(false));
    },
    [dispatch, actions]
  );

  const services = getServiceItemsFromCart(state).map((cartItem) => ({
    uuid: cartItem.service.uuid,
    quantity: cartItem.quantity,
  }));

  const pricingForms = getPricingFormItemsFromCart(state).map((cartItem) => ({
    uuid: cartItem.pricingForm.uuid,
  }));

  const {
    data: timeSlots,
    isError,
    isFetched,
    isFetching,
    isLoading,
    period,
    increaseTimespanByWeeks,
    increaseTimespanToFullMonth,
  } = useTimeSlotsV2({
    token: state.token,
    services,
    pricingForms,
    onSuccess: (data) => {
      if (data && (!selectedDate || !data[selectedDate])) {
        const dateToSelect = findDateToSelect(data);
        if (dateToSelect) {
          setDate(dateToSelect);
        } else {
          dispatch(actions.setUnavailableDateError(true));
        }
      }
    },
  });

  const shouldDisableDate = (day: Moment) => {
    return timeSlots ? !timeSlots[day.format('YYYY-MM-DD')]?.length : false;
  };

  const onDateChange = useCallback(
    (newDate: Moment | null) => {
      setDate(newDate ? newDate.format('YYYY-MM-DD') : '');
      setIsOpen(false);
    },
    [setDate]
  );

  const isExactTime = orgData?.defaultArrivalWindow === 0;

  const timeslotsUnavailable = isError || (isFetched && state.unavailableDateError);

  const isNextButtonDisabled = !(state.arrivalWindow.date && state.arrivalWindow.time);

  const handleBackClick = () => {
    dispatch(actions.hideWarningMessage());
    changeStep('contact-details');
    dispatch(actions.resetArrivalTime());
  };

  const handleNextClick = () => {
    dispatch(actions.hideWarningMessage());
    changeStep('confirmation');
  };

  const fetchNextPeriod = () => {
    increaseTimespanByWeeks(4);
  };

  const getTimeslotsForSelectedMonth = (month: Moment) => {
    const monthInView = month.get('month');
    const fetchedMonth = period.toDate.getMonth();
    const currentMonthFetched = monthInView === fetchedMonth;
    if (currentMonthFetched && !period.limitReached) {
      increaseTimespanToFullMonth();
    }
  };

  const openCalendar = () => {
    setIsOpen(true);
    if (selectedDate) {
      getTimeslotsForSelectedMonth(moment(selectedDate));
    }
  };

  useEffect(() => {
    if (timeSlots && !isLoading && isFetched && !isError) {
      const hasAnyTimeslot = Object.keys(timeSlots).some((key) => timeSlots[key]!.length > 0);
      if (!hasAnyTimeslot) {
        if (period.limitReached) {
          dispatch(actions.setUnavailableDateError(true));
        } else {
          increaseTimespanByWeeks(4);
        }
      }
    }
  }, [isLoading, timeSlots, isFetched, isError, period, dispatch, actions, increaseTimespanByWeeks]);

  return (
    <Layout
      Header={<StepProgressBar currentStep={4} stepCount={5} />}
      ContentComponent={
        <>
          <WarningMessage />
          <BackArrowButton onClick={handleBackClick} />
          <Typography gutterBottom variant="h5">
            Pick an arrival window
          </Typography>
          <ScrollableContent>
            <>
              <Box mt={1.5}>
                <InlineDatePicker
                  data-testid="date-picker"
                  classes={{ root: input }}
                  selectedDate={selectedDate}
                  fullWidth
                  onDateChange={onDateChange}
                  minDate={period.begginingDate}
                  maxDate={isFetching ? period.fromDate : period.toDate}
                  disabled={timeslotsUnavailable || isFetching}
                  shouldDisableDate={shouldDisableDate}
                  onMonthChange={getTimeslotsForSelectedMonth}
                  variant="outlined"
                  renderDay={(
                    day: Moment,
                    _selected: boolean,
                    isInCurrentMonth: boolean,
                    dayComponent: React.ReactChild
                  ) => {
                    const notFetched = day.toDate() >= period.fromDate && day.toDate() <= period.toDate;
                    const shouldShowSkeleton = isLoading && isInCurrentMonth && notFetched;
                    return shouldShowSkeleton ? <Skeleton variant="circle" width={40} height={36} /> : dayComponent;
                  }}
                  format={DATE_FORMAT}
                  KeyboardButtonProps={{
                    'data-testid': 'date-icon',
                    onFocus: () => openCalendar(),
                  }}
                  PopoverProps={{
                    disableRestoreFocus: true,
                    'data-testid': 'date-popover',
                    onClose: () => setIsOpen(false),
                  }}
                  InputProps={{
                    onFocus: () => openCalendar(),
                  }}
                  open={isOpen}
                />
              </Box>
              <DayPicker
                fetchNextPeriod={fetchNextPeriod}
                timeSlots={timeSlots || {}}
                startDate={period.begginingDate}
                lastDate={period.toDate}
                onSelect={setDate}
                selectedDate={selectedDate ? moment(selectedDate) : undefined}
                limitReached={period.limitReached}
              />
              <TimeSlots
                date={selectedDate}
                dateFrom={period.begginingDate}
                dateTo={period.toDate}
                isExactTime={isExactTime}
                timeSlots={timeSlots || {}}
                isError={isError || state.unavailableDateError}
                isLoading={!selectedDate && isLoading}
              />
            </>
          </ScrollableContent>
        </>
      }
      Buttons={
        <Button
          version="v2"
          onClick={handleNextClick}
          size="medium"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={isNextButtonDisabled}
        >
          Next
        </Button>
      }
    />
  );
};
